import React from 'react';
import {
  UserCircleIcon,
  CameraIcon,
  LockClosedIcon,
  PhotoIcon,
  EyeIcon,
  KeyIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowRightIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  CogIcon,
  UserIcon,
  EnvelopeIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';

export default function MyProfileHelp() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg">
              <UserCircleIcon className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">My Profile</h1>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Manage your personal information, security settings, and profile appearance.
            Keep your account secure and up-to-date with comprehensive profile management tools.
          </p>
        </div>

        {/* System Overview */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-8 py-6">
            <div className="flex items-center">
              <UserIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Profile Management Overview</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-6 border border-emerald-200">
                <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center mb-4">
                  <UserCircleIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Personal Information</h3>
                <p className="text-gray-600">
                  View and manage your contact details, role, and professional information.
                </p>
              </div>

              <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-6 border border-teal-200">
                <div className="w-12 h-12 bg-gradient-to-r from-teal-500 to-cyan-600 rounded-xl flex items-center justify-center mb-4">
                  <CameraIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Profile Picture</h3>
                <p className="text-gray-600">
                  Upload photos or take pictures with your camera for a professional appearance.
                </p>
              </div>

              <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl p-6 border border-cyan-200">
                <div className="w-12 h-12 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center mb-4">
                  <LockClosedIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Security Settings</h3>
                <p className="text-gray-600">
                  Change passwords, manage account security, and protect your information.
                </p>
              </div>
            </div>

            {/* Profile Management Flow */}
            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900 text-center mb-6">Profile Management Flow</h3>
              <div className="flex flex-col md:flex-row items-center justify-center space-y-4 md:space-y-0 md:space-x-8">
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-full flex items-center justify-center mb-2">
                    <EyeIcon className="h-8 w-8 text-white" />
                  </div>
                  <span className="text-sm font-semibold text-gray-900">View Profile</span>
                  <span className="text-xs text-gray-600">Review Information</span>
                </div>

                <ArrowRightIcon className="h-6 w-6 text-gray-400 hidden md:block" />
                <div className="md:hidden w-px h-8 bg-gray-300"></div>

                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-teal-500 to-cyan-600 rounded-full flex items-center justify-center mb-2">
                    <PhotoIcon className="h-8 w-8 text-white" />
                  </div>
                  <span className="text-sm font-semibold text-gray-900">Update Picture</span>
                  <span className="text-xs text-gray-600">Upload or Capture</span>
                </div>

                <ArrowRightIcon className="h-6 w-6 text-gray-400 hidden md:block" />
                <div className="md:hidden w-px h-8 bg-gray-300"></div>

                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full flex items-center justify-center mb-2">
                    <KeyIcon className="h-8 w-8 text-white" />
                  </div>
                  <span className="text-sm font-semibold text-gray-900">Change Password</span>
                  <span className="text-xs text-gray-600">Security Update</span>
                </div>

                <ArrowRightIcon className="h-6 w-6 text-gray-400 hidden md:block" />
                <div className="md:hidden w-px h-8 bg-gray-300"></div>

                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mb-2">
                    <CheckCircleIcon className="h-8 w-8 text-white" />
                  </div>
                  <span className="text-sm font-semibold text-gray-900">Save Changes</span>
                  <span className="text-xs text-gray-600">Complete Update</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Profile Features */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-teal-600 to-cyan-600 px-8 py-6">
            <div className="flex items-center">
              <CogIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Profile Features & Information</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl p-6 border border-emerald-200">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center mr-3">
                      <UserIcon className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">Personal Information</h3>
                  </div>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Full name and professional display</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Job role and department information</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Email address and contact details</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Telephone number and location</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-2xl p-6 border border-teal-200">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-gradient-to-r from-teal-500 to-cyan-600 rounded-lg flex items-center justify-center mr-3">
                      <EnvelopeIcon className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">Contact Actions</h3>
                  </div>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Direct messaging to team members</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Location display and availability</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Professional communication tools</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Team collaboration features</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-2xl p-6 border border-cyan-200">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center mr-3">
                      <PhotoIcon className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">Profile Picture Options</h3>
                  </div>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>File upload from device storage</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Live camera capture functionality</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Automatic image processing and optimization</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Professional appearance standards</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center mr-3">
                      <KeyIcon className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">Password Security</h3>
                  </div>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Secure password change process</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Current password verification</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Password strength requirements</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Automatic session management</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Step-by-Step Guide */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-8 py-6">
            <div className="flex items-center">
              <ArrowRightIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Complete Profile Management Guide</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="space-y-12">
              {/* Step 1: Access Profile */}
              <div className="flex items-start">
                <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mr-8">
                  <span className="text-white font-bold text-2xl">1</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">Access Your Profile</h3>
                  <p className="text-gray-600 mb-6 text-lg">
                    Navigate to your profile page to view and manage your personal information and account settings.
                  </p>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <div className="bg-emerald-50 rounded-xl p-6 border border-emerald-200">
                        <h4 className="text-lg font-semibold text-emerald-900 mb-3">Profile Layout</h4>
                        <ul className="space-y-2 text-emerald-700">
                          <li>• Professional header with gradient background</li>
                          <li>• Profile picture prominently displayed</li>
                          <li>• Contact information and action buttons</li>
                          <li>• Detailed information cards below</li>
                        </ul>
                      </div>

                      <div className="bg-teal-50 rounded-xl p-6 border border-teal-200">
                        <h4 className="text-lg font-semibold text-teal-900 mb-3">Navigation</h4>
                        <ul className="space-y-2 text-teal-700">
                          <li>• Available from both manager and staff navigation</li>
                          <li>• Direct access through menu options</li>
                          <li>• Mobile-responsive design</li>
                          <li>• Consistent with app navigation patterns</li>
                        </ul>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <div className="bg-cyan-50 rounded-xl p-6 border border-cyan-200">
                        <h4 className="text-lg font-semibold text-cyan-900 mb-3">Information Display</h4>
                        <ul className="space-y-2 text-cyan-700">
                          <li>• Real-time data loading from server</li>
                          <li>• Automatic profile picture processing</li>
                          <li>• Formatted contact information</li>
                          <li>• Role and department details</li>
                        </ul>
                      </div>

                      <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                        <h4 className="text-lg font-semibold text-blue-900 mb-3">Action Buttons</h4>
                        <ul className="space-y-2 text-blue-700">
                          <li>• Message button for team communication</li>
                          <li>• Location display for availability</li>
                          <li>• Profile picture change options</li>
                          <li>• Password management access</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 2: Update Profile Picture */}
              <div className="flex items-start">
                <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-r from-teal-500 to-cyan-600 rounded-2xl flex items-center justify-center mr-8">
                  <span className="text-white font-bold text-2xl">2</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">Update Your Profile Picture</h3>
                  <p className="text-gray-600 mb-6 text-lg">
                    Choose between uploading an existing photo or taking a new picture with your camera.
                  </p>

                  <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-2xl p-8 border border-teal-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div>
                        <h4 className="text-xl font-semibold text-gray-900 mb-4">File Upload Method</h4>
                        <ul className="space-y-3">
                          <li className="flex items-start">
                            <PhotoIcon className="h-5 w-5 text-teal-500 mr-3 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">Click "Change" button to open file selector</span>
                          </li>
                          <li className="flex items-start">
                            <PhotoIcon className="h-5 w-5 text-teal-500 mr-3 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">Select image file from your device</span>
                          </li>
                          <li className="flex items-start">
                            <PhotoIcon className="h-5 w-5 text-teal-500 mr-3 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">Automatic upload and processing</span>
                          </li>
                          <li className="flex items-start">
                            <PhotoIcon className="h-5 w-5 text-teal-500 mr-3 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">Page refresh to display new image</span>
                          </li>
                        </ul>
                      </div>

                      <div>
                        <h4 className="text-xl font-semibold text-gray-900 mb-4">Camera Capture Method</h4>
                        <ul className="space-y-3">
                          <li className="flex items-start">
                            <CameraIcon className="h-5 w-5 text-cyan-500 mr-3 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">Click "Take Picture" button</span>
                          </li>
                          <li className="flex items-start">
                            <CameraIcon className="h-5 w-5 text-cyan-500 mr-3 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">Grant camera permissions when prompted</span>
                          </li>
                          <li className="flex items-start">
                            <CameraIcon className="h-5 w-5 text-cyan-500 mr-3 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">Position yourself in frame and click "Take Picture"</span>
                          </li>
                          <li className="flex items-start">
                            <CameraIcon className="h-5 w-5 text-cyan-500 mr-3 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">Automatic capture and upload processing</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 3: Change Password */}
              <div className="flex items-start">
                <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center mr-8">
                  <span className="text-white font-bold text-2xl">3</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">Change Your Password</h3>
                  <p className="text-gray-600 mb-6 text-lg">
                    Securely update your password with proper validation and confirmation.
                  </p>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl p-8 border border-cyan-200">
                      <div className="flex items-center mb-6">
                        <KeyIcon className="h-8 w-8 text-cyan-500 mr-3" />
                        <h4 className="text-xl font-semibold text-cyan-900">Password Requirements</h4>
                      </div>
                      <ul className="space-y-3">
                        <li className="flex items-start">
                          <CheckCircleIcon className="h-4 w-4 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-cyan-700">Minimum 8 characters in length</span>
                        </li>
                        <li className="flex items-start">
                          <CheckCircleIcon className="h-4 w-4 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-cyan-700">New password must match confirmation</span>
                        </li>
                        <li className="flex items-start">
                          <CheckCircleIcon className="h-4 w-4 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-cyan-700">Current password verification required</span>
                        </li>
                        <li className="flex items-start">
                          <CheckCircleIcon className="h-4 w-4 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-cyan-700">Secure server-side processing</span>
                        </li>
                      </ul>
                    </div>

                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-200">
                      <div className="flex items-center mb-6">
                        <ShieldCheckIcon className="h-8 w-8 text-blue-500 mr-3" />
                        <h4 className="text-xl font-semibold text-blue-900">Security Process</h4>
                      </div>
                      <div className="space-y-4">
                        <div className="bg-white rounded-lg p-4 border border-blue-200">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-gray-900">Step 1: Initiate Change</span>
                            <span className="text-sm text-blue-600 font-semibold">Click Button</span>
                          </div>
                          <p className="text-sm text-gray-600">Click "Change Password" to reveal the form</p>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-blue-200">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-gray-900">Step 2: Enter Details</span>
                            <span className="text-sm text-indigo-600 font-semibold">Fill Form</span>
                          </div>
                          <p className="text-sm text-gray-600">Provide current and new passwords</p>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-blue-200">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-gray-900">Step 3: Submit & Logout</span>
                            <span className="text-sm text-purple-600 font-semibold">Auto Logout</span>
                          </div>
                          <p className="text-sm text-gray-600">Successful change triggers automatic logout</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 4: Complete Updates */}
              <div className="flex items-start">
                <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mr-8">
                  <span className="text-white font-bold text-2xl">4</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">Complete Profile Updates</h3>
                  <p className="text-gray-600 mb-6 text-lg">
                    Review your changes and ensure all updates are properly saved and applied.
                  </p>

                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-200">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="text-center">
                        <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mx-auto mb-3">
                          <EyeIcon className="h-6 w-6 text-white" />
                        </div>
                        <h4 className="font-semibold text-gray-900 mb-2">Review Changes</h4>
                        <p className="text-sm text-gray-600">Verify all information is correct before saving</p>
                      </div>

                      <div className="text-center">
                        <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center mx-auto mb-3">
                          <CheckCircleIcon className="h-6 w-6 text-white" />
                        </div>
                        <h4 className="font-semibold text-gray-900 mb-2">Confirm Updates</h4>
                        <p className="text-sm text-gray-600">Submit changes and wait for confirmation</p>
                      </div>

                      <div className="text-center">
                        <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl flex items-center justify-center mx-auto mb-3">
                          <ShieldCheckIcon className="h-6 w-6 text-white" />
                        </div>
                        <h4 className="font-semibold text-gray-900 mb-2">Security Applied</h4>
                        <p className="text-sm text-gray-600">Changes are securely saved and applied</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Technical Features */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-teal-600 to-cyan-600 px-8 py-6">
            <div className="flex items-center">
              <CogIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Technical Features & Security</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-6 border border-emerald-200">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center mr-3">
                    <CameraIcon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Camera Integration</h3>
                </div>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li>• WebRTC camera access</li>
                  <li>• Real-time video streaming</li>
                  <li>• Canvas-based image capture</li>
                  <li>• Automatic format conversion</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-6 border border-teal-200">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-teal-500 to-cyan-600 rounded-lg flex items-center justify-center mr-3">
                    <PhotoIcon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Image Processing</h3>
                </div>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li>• Base64 encoding for storage</li>
                  <li>• Automatic resizing</li>
                  <li>• Format optimization</li>
                  <li>• Secure file upload</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl p-6 border border-cyan-200">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center mr-3">
                    <LockClosedIcon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Password Security</h3>
                </div>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li>• Server-side validation</li>
                  <li>• Secure password hashing</li>
                  <li>• Session management</li>
                  <li>• Automatic logout on change</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center mr-3">
                    <ShieldCheckIcon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Data Protection</h3>
                </div>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li>• Encrypted data transmission</li>
                  <li>• Secure API endpoints</li>
                  <li>• User authentication</li>
                  <li>• Privacy compliance</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-200">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center mr-3">
                    <DevicePhoneMobileIcon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Cross-Platform</h3>
                </div>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li>• Mobile-responsive design</li>
                  <li>• Touch-friendly interfaces</li>
                  <li>• Camera API compatibility</li>
                  <li>• File upload support</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-200">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg flex items-center justify-center mr-3">
                    <ComputerDesktopIcon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Desktop Features</h3>
                </div>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li>• Full camera integration</li>
                  <li>• Advanced file selection</li>
                  <li>• Keyboard navigation</li>
                  <li>• Large screen optimization</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Troubleshooting */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-red-600 to-orange-600 px-8 py-6">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Troubleshooting & Best Practices</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="bg-red-50 rounded-xl p-6 border border-red-200">
                  <h3 className="text-lg font-bold text-red-900 mb-3">Common Issues</h3>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-red-800 mb-2">Camera Not Working</h4>
                      <ul className="text-sm text-red-700 space-y-1">
                        <li>• Grant camera permissions in browser</li>
                        <li>• Check if camera is being used by another app</li>
                        <li>• Try refreshing the page</li>
                        <li>• Use file upload as alternative</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold text-red-800 mb-2">Password Change Failed</h4>
                      <ul className="text-sm text-red-700 space-y-1">
                        <li>• Verify current password is correct</li>
                        <li>• Ensure new password meets requirements</li>
                        <li>• Check internet connection</li>
                        <li>• Try again after page refresh</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 rounded-xl p-6 border border-yellow-200">
                  <h3 className="text-lg font-bold text-yellow-900 mb-3">Upload Issues</h3>
                  <ul className="space-y-2 text-sm text-yellow-700">
                    <li>• Supported formats: JPG, PNG, GIF</li>
                    <li>• Maximum file size: Check system limits</li>
                    <li>• Ensure stable internet connection</li>
                    <li>• Try smaller image files if upload fails</li>
                    <li>• Clear browser cache if issues persist</li>
                  </ul>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                  <h3 className="text-lg font-bold text-blue-900 mb-3">Best Practices</h3>
                  <ul className="space-y-3 text-sm text-blue-700">
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Use professional photos for your profile picture</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Change passwords regularly for security</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Keep contact information current and accurate</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Use strong, unique passwords</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Regularly review and update profile information</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                  <h3 className="text-lg font-bold text-green-900 mb-3">Security Tips</h3>
                  <ul className="space-y-2 text-sm text-green-700">
                    <li>• Never share your password with others</li>
                    <li>• Use camera in private, well-lit areas</li>
                    <li>• Log out after making security changes</li>
                    <li>• Report any suspicious account activity</li>
                    <li>• Keep recovery information up to date</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Platform-Specific Usage */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-6">
            <div className="flex items-center">
              <DevicePhoneMobileIcon className="h-8 w-8 text-white mr-4" />
              <ComputerDesktopIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Platform-Specific Features</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Mobile Experience</h3>
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                    <h4 className="text-lg font-semibold text-blue-900 mb-3">Touch-Optimized Interface</h4>
                    <ul className="space-y-2 text-blue-700">
                      <li>• Large touch targets for buttons</li>
                      <li>• Swipe gestures for navigation</li>
                      <li>• Mobile camera integration</li>
                      <li>• Responsive form layouts</li>
                      <li>• Optimized for small screens</li>
                    </ul>
                  </div>

                  <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-200">
                    <h4 className="text-lg font-semibold text-indigo-900 mb-3">Mobile Camera Features</h4>
                    <ul className="space-y-2 text-indigo-700">
                      <li>• Native camera app integration</li>
                      <li>• Touch-to-focus functionality</li>
                      <li>• Front/rear camera switching</li>
                      <li>• Automatic image stabilization</li>
                      <li>• Mobile-optimized capture interface</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Desktop Experience</h3>
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200">
                    <h4 className="text-lg font-semibold text-purple-900 mb-3">Advanced Camera Controls</h4>
                    <ul className="space-y-2 text-purple-700">
                      <li>• Full WebRTC camera access</li>
                      <li>• Resolution and quality settings</li>
                      <li>• Multiple camera device selection</li>
                      <li>• Real-time preview adjustments</li>
                      <li>• Professional capture controls</li>
                    </ul>
                  </div>

                  <div className="bg-gradient-to-r from-pink-50 to-red-50 rounded-xl p-6 border border-pink-200">
                    <h4 className="text-lg font-semibold text-pink-900 mb-3">File Management</h4>
                    <ul className="space-y-2 text-pink-700">
                      <li>• Drag-and-drop file uploads</li>
                      <li>• Multiple file format support</li>
                      <li>• Batch processing capabilities</li>
                      <li>• Advanced file selection dialogs</li>
                      <li>• Local file system integration</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}